import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// Phase 37 — operator-facing theme toggle.
//
// Storage rules:
//   - localStorage key 'kopechka.admin.theme' holds the operator's choice
//     ('light' | 'dark'). Persisted across reloads.
//   - When unset, fall back to OS preference via prefers-color-scheme.
//   - The class is applied to <html>, so styles.css can use
//     :root[data-theme="dark"] selectors without a flash on first paint.
//
// applyInitialTheme() runs from main.tsx BEFORE React mounts so the
// document already carries the right data-theme attribute when the first
// frame paints. The provider re-syncs on mount and exposes a toggle.

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'kopechka.admin.theme';

function readStoredTheme(): ThemeMode | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
    return null;
  } catch {
    return null;
  }
}

function detectSystemTheme(): ThemeMode {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch {
    // ignore — fall through to default
  }
  return 'light';
}

function resolveInitialTheme(): ThemeMode {
  const stored = readStoredTheme();
  if (stored) return stored;
  return detectSystemTheme();
}

function applyThemeAttribute(theme: ThemeMode): void {
  try {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.style.colorScheme = theme;
  } catch {
    // ignore — non-DOM environments (tests) do not care about <html>.
  }
}

export function applyInitialTheme(): void {
  if (typeof document === 'undefined') return;
  applyThemeAttribute(resolveInitialTheme());
}

interface ThemeContextValue {
  readonly theme: ThemeMode;
  readonly setTheme: (next: ThemeMode) => void;
  readonly toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<ThemeMode>(() => resolveInitialTheme());

  useEffect(() => {
    applyThemeAttribute(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore — storage may be disabled (private mode); UI still works.
    }
  }, [theme]);

  const setTheme = useCallback((next: ThemeMode) => setThemeState(next), []);
  const toggleTheme = useCallback(
    () => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    [],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
